import { isRenderedInPortalFrame } from '@qualtrics-portals/portal-checker';
import bundleServiceFactory from './bundle-service';
import {
  checkIfPageIsValidInPortal,
  getAvailablePortals,
  isPortalsApp,
} from './portals-service';
import {
  initializeMetricsService,
  initializeBaseRumMetaData,
  initializeRumMetaData,
} from './metrics-service';
import scriptLoadServiceFactory from './script-load-service';
import { messageService } from './message-service';
import { shouldHideEntireNavInChildIframe } from './program-child-checker-service';
import { isExportPage } from './tool-check';
import { setBadRequestDatacenter } from './bad-request-reporter';
import { tractiveInit } from '../../../v1/js/inject-tractive';

// Per-brand flags evaluated by global-wrapper-service in Feature Flipper.
const getAmplitudeFlags = (features = {}) => ({
  // Report every request a page sends to Amplitude (RUM event with wasAmplitudeSent: true)
  isMonitoringEnabled: !!features['GlobalWrapper.AmplitudeMonitoring'],
  // Install a no-op window.tractive instead of loading tractive.min.js
  isTractiveNoopEnabled: !!features.BlockAmplitude,
  // Drop requests to Amplitude hosts made through fetch / XHR / sendBeacon (only together with BlockAmplitude)
  isRequestBlockingEnabled:
    !!features['GlobalWrapper.AmplitudeRequestBlocking'],
});

const preInitializationRUMMetadata = {
  userId: '',
  brandId: '',
  dc: window.Qualtrics?.state?.datacenter,
};

const initialize = (language, context, pageId, loadTabs, isExternalAppMode) => {
  const bundleService = bundleServiceFactory();

  const config = {};
  initializeBaseRumMetaData(preInitializationRUMMetadata);
  initializeMetricsService();
  const translationsPromise = messageService.initialize(language);
  const bundlePromise = bundleService.getBundle();
  // Any failure (bundle rejected, or reading the flags threw) falls back to loading the real script.
  bundlePromise
    .then((bundleData) =>
      tractiveInit(getAmplitudeFlags(bundleData?.features), {
        pageId,
        context,
      }),
    )
    .catch(() => tractiveInit());
  return bundlePromise
    .then((bundleData) => {
      setBadRequestDatacenter(bundleData.system?.dc);
      const user = bundleData.user;
      const brand = bundleData.brand;

      const userData = {
        userId: user.userId,
        brandId: user.brandId,
        language: language,
        email: user.email,
        userName: user.userName,
        firstName: user.firstName,
        lastName: user.lastName,
        displayName: user.firstName + ' ' + user.lastName,
        accountType: user.accountType,
        isRestrictedNavigation: user.accountType === 'UT_SHARED',
        accountCreationDate: user.accountCreationDate,
        brandName: brand.brandName,
        brandType: brand.brandType,
      };

      const rumMetadata = {
        userId: user.userId,
        brandId: user.brandId,
        dc: bundleData.system?.dc,
      };

      config.restrictedNavigation = userData.isRestrictedNavigation;
      config.userData = userData;

      initializeRumMetaData(rumMetadata);

      return bundleData;
    })
    .then((bundleData) => {
      config.portals = [];
      const portalsConfig = getAvailablePortals(language)
        .then((portals) => {
          checkIfPageIsValidInPortal(
            portals,
            context,
            config.userData.userId,
            config.restrictedNavigation,
            pageId,
            !!bundleData.features['Portals.EnablePortalsChooser'],
            !!bundleData.features['Portals.EnableGWFallbackBlocking'],
          );
          config.portals = portals;
        })
        .catch(() => {
          config.portals = [];
        });

      const mainConfig = translationsPromise.then(() => {
        return loadTabs(context, bundleData, pageId).then((_) => {
          const { dc } = bundleData.system || {};
          const isB1OrG1 =
            dc === 'b1-prv' || dc === 'a1-prv' || dc === 'g1-cmh';

          // TODO: Fix this hack for the Jira pulse NAV-386
          const externalModulesProps = {
            context,
            isFullstoryPilotEnabled: !!bundleData.features.fullstory,
            samProxySupportModalEnabled:
              !!bundleData.features.GW_SAM_PROXY_SUPPORT_MODAL,
            userId: config.userData.userId,
            isPortalsApplication: isPortalsApp(context.contextType),
            pageId,
            webVitalsEnabled: isB1OrG1,
            styleAuditorEnabled:
              !!bundleData.features['GlobalWrapper.QStyleAuditor'],
            invokeAsidFlow: !!bundleData.features['IDS.KeepaliveAsidFlow'],
          };

          const isHomepageUIFeatureFlipperEnabled =
            !!bundleData.features['Homepage.EnableUI'];

          config.isCustomLogoEnabled =
            !!bundleData.features['GlobalWrapper.EnableCustomLogoHeader'] ||
            false;

          config.homeDefaultDestination = isHomepageUIFeatureFlipperEnabled
            ? '/homepage/ui'
            : '/Q/MyProjectsSection';

          config.isFreeAccount = !!bundleData.features.PG_IsFreeAccount;

          config.enableSkipToMainContent =
            !!bundleData.features['GlobalWrapper.EnableSkipToMainContent'] ||
            false;

          config.displayHelpModalInPortal =
            !!bundleData.features['Portals.EnablePortalsSettings'] || false;
          config.enableExternalPortals =
            !!bundleData.features['Portals.EnableExternalPortals'] || false;

          config.displayHelpModalInPortal =
            !!bundleData.features['Portals.EnablePortalsSettings'] || false;

          config.isImportantMessagesEnabled =
            !!bundleData.features['GlobalWrapper.IMBA.Enable'] || false;

          config.areNewTagsEnabled =
            !!bundleData.features['GlobalWrapper.EnableNewTag'] || false;

          config.isFavoritePbaEnabled =
            !!bundleData.features['Portals.EnableFavoritePba'] || false;

          config.isCommandPaletteEnabled =
            !!bundleData.features['GlobalWrapper.EnableCommandPalette'] ||
            false;

          config.isDeepStorageInspectionEnabled =
            !!bundleData.features['GlobalWrapper.DeepStorageInspection'];

          config.v2Info = {
            pageId: pageId,
            context: context,
          };

          config.DC = dc;

          return [config, externalModulesProps];
        });
      });

      return Promise.all([mainConfig, portalsConfig]).then(
        ([[config, externalModulesProps]]) => {
          loadExternalModules(
            externalModulesProps.context,
            externalModulesProps.isFullstoryPilotEnabled,
            externalModulesProps.samProxySupportModalEnabled,
            externalModulesProps.userId,
            externalModulesProps.isPortalsApplication,
            externalModulesProps.pageId,
            externalModulesProps.webVitalsEnabled,
            externalModulesProps.styleAuditorEnabled,
            externalModulesProps.invokeAsidFlow,
            isExternalAppMode,
            config.DC,
          );
          return config;
        },
      );
    });
};

const loadExternalModules = (
  context,
  fullstoryPilotEnabled,
  samProxySupportModalEnabled,
  userId,
  isPortalsApplication,
  pageId,
  webVitalsEnabled,
  styleAuditorEnabled,
  invokeAsidFlow,
  isExternalPortalMode,
  dc,
) => {
  const scriptLoadService = scriptLoadServiceFactory();
  // eslint-disable-next-line testing-library/render-result-naming-convention
  const isInPortal = isRenderedInPortalFrame();
  const shouldHideNav = shouldHideEntireNavInChildIframe();
  const isNavHiddenInIframe = isInPortal || shouldHideNav;
  if (!isNavHiddenInIframe && !isExternalPortalMode) {
    // Do not wait on promise from script loading
    scriptLoadService.loadScript(
      '/app/notifications-bell/main.js',
      'script-notifications-modal',
    );
  }

  if (!isNavHiddenInIframe) {
    scriptLoadService.loadScript('/login/proxy-approval-client/bundle.min.js');
    scriptLoadService.loadScript(
      '/login/keepalive-client/bundle.min.js',
      'keepalive-script',
      { asidFlow: invokeAsidFlow, dataCenter: dc },
    );
  }

  if (!isNavHiddenInIframe) {
    scriptLoadService.loadScript(
      '/dg-service/scripts/free_account_prompts.min.js',
    );

    if (!isPortalsApplication && !isExternalPortalMode) {
      if (samProxySupportModalEnabled) {
        scriptLoadService.loadScript(
          '/app/support-modal/bundle.js',
          'script-support-modal',
          context,
        );
      } else {
        scriptLoadService.loadScript(
          '/static/support-modal/bundle.js',
          'script-support-modal',
          context,
        );
      }
    }

    if (webVitalsEnabled && !isExportPage()) {
      scriptLoadService.loadScript(
        '/static/q-web-vitals/q-web-vitals.js',
        'script-q-web-vitals',
        { contextType: context.contextType, pageId, dc },
      );
    }

    if (styleAuditorEnabled && !isExportPage()) {
      scriptLoadService.loadScript(
        '/static/q-style-auditor/q-style-auditor.min.js',
        'script-q-style-auditor',
        { contextType: context.contextType, pageId, dc },
      );
    }
  }

  scriptLoadService.loadScript(
    '/static/home-page-utils/homepage-recently-visited.min.js',
    'script-homepage-recently-visited',
    context,
  );

  if (fullstoryPilotEnabled) {
    scriptLoadService.loadScript(
      '/static/fullstory-snippet/qualtrics-fs-snippet.js',
      'script-fullstory',
      { userId: userId },
    );
  }

  const siteInterceptUrl =
    '/static/site-intercept-global-wrapper-snippets/snippet.js';
  scriptLoadService
    .loadScript(siteInterceptUrl, 'script-site-intercept', { extRefId: userId })
    .catch((error) =>
      console.error(
        `Global Wrapper failed to load ${siteInterceptUrl}:`,
        error,
      ),
    );
};

export { initialize };
